<template>
  <article id="processing-batch">
    <h1>🌐 批处理</h1>

    <section id="batch-intro">
      <h2>一、简介</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;辐射校正批处理模块用于实现多景全色与多光谱影像的自动化辐射定标与（可选）几何校正处理。
        该功能可在无需手动逐景处理的情况下，自动识别输入目录下的影像文件，批量执行辐射定标、输出表观辐亮度图像及定标报告，显著提高数据生产效率。
      </p>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;本模块具备以下核心优势：
      </p>
      <ul>
        <li>支持多景影像的自动化批量处理，无需人工干预；</li>
        <li>自动识别输入目录下的影像文件，智能匹配定标系数；</li>
        <li>可选配几何校正功能，实现从原始数据到地理配准的一站式处理；</li>
        <li>自动生成详细的处理报告与日志，便于质量控制与追溯。</li>
      </ul>
    </section>

    <section id="batch-interface">
      <h2>二、功能界面</h2>
      <div class="image-section">
        <div class="image-container">
          <img src="@/assets/documentation/pichuli.png" alt="批处理界面" class="software-image" />
          <p class="image-caption">图8：批处理界面 - 实现多景影像的自动化辐射定标与几何校正</p>
        </div>
      </div>
      <p>界面组成部分说明如下：</p>
      <table>
        <thead>
          <tr>
            <th>功能区域</th>
            <th>控件</th>
            <th>说明</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>输入路径</td>
            <td>文件夹选择框</td>
            <td>选择包含原始影像的目录</td>
          </tr>
          <tr>
            <td>卫星载荷</td>
            <td>下拉菜单</td>
            <td>选择对应的卫星与载荷（如 GF5B_AHSI_VN/SW）</td>
          </tr>
          <tr>
            <td>包含几何校正</td>
            <td>复选框</td>
            <td>勾选后将在辐射定标后自动进行几何校正</td>
          </tr>
          <tr>
            <td>操作按钮</td>
            <td>"处理""帮助""退出"</td>
            <td>分别用于开始批处理、查看帮助、退出程序</td>
          </tr>
        </tbody>
      </table>
      <p class="note">🚀 批处理模块支持自动识别多种影像格式，包括 .tiff、.dat、.hdr 等，大幅提升数据处理效率。</p>
    </section>

    <section id="batch-params">
      <h2>三、用户参数</h2>
      <table>
        <thead>
          <tr>
            <th>参数名</th>
            <th>类型</th>
            <th>默认值</th>
            <th>说明</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>输入路径</td>
            <td>文件夹路径</td>
            <td>无</td>
            <td>用户需选择包含影像文件的文件夹（支持子目录扫描）</td>
          </tr>
          <tr>
            <td>卫星载荷</td>
            <td>字符串</td>
            <td>GF5B_AHSI_VN/SW</td>
            <td>选择对应的卫星传感器以匹配定标系数</td>
          </tr>
          <tr>
            <td>包含几何校正</td>
            <td>布尔</td>
            <td>√</td>
            <td>若勾选，则同时执行几何校正步骤</td>
          </tr>
          <tr>
            <td>输出目录</td>
            <td>自动生成</td>
            <td>/output/</td>
            <td>自动生成与输入路径平行的输出目录结构</td>
          </tr>
        </tbody>
      </table>
      <p class="note">🔍 提示：批处理模块会自动扫描输入目录及其子目录，识别所有符合格式的影像文件进行处理。</p>
    </section>

    <section id="batch-steps">
      <h2>四、操作步骤</h2>
      <ol>
        <li>
          <strong>选择输入路径</strong>
          <p>点击"打开"，选择包含多景影像的文件夹。系统会自动识别其中的原始数据文件（如 .tiff, .dat, .hdr 等）。</p>
        </li>
        <li>
          <strong>选择卫星载荷</strong>
          <p>从下拉框中选择与数据对应的卫星载荷，例如 GF5B_AHSI_VNSW、GF6_WFV 等。</p>
        </li>
        <li>
          <strong>设置是否包含几何校正</strong>
          <p>若需要在批量辐射校正后进行几何配准，可勾选"包含几何校正"。</p>
        </li>
        <li>
          <strong>开始处理</strong>
          <p>点击"处理"按钮，程序将自动执行以下步骤：</p>
          <ul>
            <li>读取输入目录下的所有有效影像文件</li>
            <li>自动加载对应的定标系数文件</li>
            <li>执行辐射定标运算，生成表观辐亮度图像</li>
            <li>若勾选"包含几何校正"，则自动调用几何校正模块完成地理配准</li>
            <li>输出定标与校正报告</li>
          </ul>
        </li>
        <li>
          <strong>查看结果</strong>
          <p>处理完成后，系统会提示"批处理完成"，并在输出目录中生成结果。</p>
        </li>
      </ol>
    </section>

    <section id="batch-results">
      <h2>五、用户结果</h2>
      <table>
        <thead>
          <tr>
            <th>输出文件</th>
            <th>文件格式</th>
            <th>内容说明</th>
          </tr>
        </thead>
        <!-- <tbody>
          <tr>
            <td>表观辐亮度图像</td>
            <td>.tif</td>
            <td>各景影像的辐射定标结果</td>
          </tr>
          <tr>
            <td>几何校正影像（可选）</td>
            <td>.tif</td>
            <td>若勾选"包含几何校正"，则输出带坐标影像</td>
          </tr>
          <tr>
            <td>定标参数报告</td>
            <td>.txt / .csv</td>
            <td>每景影像对应的定标参数、系数、执行时间等</td>
          </tr>
          <tr>
            <td>批处理日志</td>
            <td>.log</td>
            <td>记录批处理过程及异常信息</td>
          </tr>
          <tr>
            <td>汇总报告</td>
            <td>.xlsx</td>
            <td>各景影像的定标统计信息与处理状态</td>
          </tr>
        </tbody> -->
      </table>
      <p class="note">✅ 批处理结果自动组织为结构化目录，便于后续数据管理与应用。</p>
      <p class="note">✅ 汇总报告提供了完整的处理统计信息，包括成功/失败景数、处理时间、质量参数等。</p>
      <p class="note">✅ 所有输出影像均保留完整的元数据信息，兼容主流遥感软件平台。</p>
    </section>
  </article>
</template>

<script>
export default {
  name: 'ProcessingBatch',
  props: {
    targetSectionId: {
      type: String,
      default: null,
    },
  },
  watch: {
    targetSectionId(newId) {
      if (newId) {
        this.$emit('scroll-to-section', newId);
      }
    },
  },
  mounted() {
    if (this.targetSectionId) {
      this.$emit('scroll-to-section', this.targetSectionId);
    }
  },
};
</script>

<style scoped>
/* 图片展示样式 */
.image-section {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.image-container {
  text-align: center;
  background: rgba(var(--card-bg), 0.8);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(var(--primary), 0.1);
  border: 1px solid rgba(var(--primary), 0.1);
}

.software-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.software-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.image-caption {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.5;
}

/* 添加计数器重置 */
article {
  counter-reset: list-item;
}

/* 重用 Document.vue 中的表格和列表样式 */
table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
  font-size: 15px;
  text-align: left;
  box-shadow: 0 0 15px rgba(var(--primary), 0.1);
  border-radius: 8px;
  overflow: hidden;
}

th,
td {
  padding: 12px 15px;
  border: 1px solid rgba(var(--primary), 0.2);
  color: var(--text-primary);
}

thead tr {
  background-color: rgba(var(--primary), 0.2);
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.5);
  font-weight: bold;
}

tbody tr {
  background-color: rgba(var(--card-bg), 0.6);
}

tbody tr:nth-of-type(even) {
  background-color: rgba(var(--card-bg), 0.8);
}

tbody tr:hover {
  background-color: rgba(var(--primary), 0.1);
  color: var(--primary);
  text-shadow: 0 0 3px rgba(var(--primary), 0.3);
}

/* List Styles (Ordered and Unordered) */
ol,
ul {
  list-style: none;
  padding-left: 20px;
  margin: 20px 0;
}

ol li,
ul li {
  margin-bottom: 10px;
  color: var(--text-primary);
  line-height: 1.6;
  position: relative;
}

ol li {
  counter-increment: list-item;
}

ol li strong,
ul li strong {
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.4);
}

ol li::before {
  content: counter(list-item) ". ";
  color: var(--secondary);
  font-weight: bold;
  position: absolute;
  left: -25px;
  top: 0;
  width: 20px;
  text-align: right;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

ul li::before {
  content: "\2705";
  color: var(--secondary);
  font-size: 1.1em;
  position: absolute;
  left: -50px;
  top: 0;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

ul li p {
  margin-left: 0;
  margin-bottom: 5px;
}

p.note {
  background-color: rgba(var(--secondary), 0.1);
  border-left: 5px solid var(--secondary);
  padding: 10px 15px;
  margin-top: 20px;
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
}
</style>

